import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { getTheme } from "../theme";

type Mode = "light" | "dark";

type ThemeModeContextValue = {
  mode: Mode;
  effectiveMode: Mode;
  setMode: (m: Mode) => void;
  toggleMode: () => void;
};

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

const STORAGE_KEY = "themeMode";
const FORCED_DARK_PATHS = ["/landing", "/login", "/signup", "/verify"]; // always dark here

export function ThemeModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<Mode>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY) as Mode | null;
      if (saved === "light" || saved === "dark") return saved;
    } catch {}
    if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches) {
      return "light";
    }
    return "dark";
  });

  // Persist mode
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, mode); } catch {}
  }, [mode]);

  // Track path changes (even across client-side navigation)
  const [path, setPath] = useState<string>(() => (typeof window !== "undefined" ? window.location.pathname : ""));
  useEffect(() => {
    if (typeof window === "undefined") return;
    const w = window as any;
    // Monkey-patch history to emit a custom event on navigation
    const wrap = (type: "pushState" | "replaceState") => {
      const orig = w.history[type];
      return function (this: History, ...args: any[]) {
        const ret = orig.apply(this, args as any);
        window.dispatchEvent(new Event("repair360:locationchange"));
        return ret;
      } as History[typeof type];
    };
    if (!w.__repair360_history_patched) {
      w.history.pushState = wrap("pushState");
      w.history.replaceState = wrap("replaceState");
      w.__repair360_history_patched = true;
    }
    const onChange = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onChange);
    window.addEventListener("repair360:locationchange", onChange);
    return () => {
      window.removeEventListener("popstate", onChange);
      window.removeEventListener("repair360:locationchange", onChange);
    };
  }, []);

  // Determine if we need to force dark by pathname
  const effectiveMode: Mode = useMemo(() => {
    const forced = FORCED_DARK_PATHS.some((p) => path === p || path.startsWith(p + "/"));
    return forced ? "dark" : mode;
  }, [mode, path]);

  const setMode = useCallback((m: Mode) => setModeState(m), []);
  const toggleMode = useCallback(() => setModeState((m) => (m === "dark" ? "light" : "dark")), []);

  const theme = useMemo(() => getTheme(effectiveMode), [effectiveMode]);

  const value = useMemo(() => ({ mode, effectiveMode, setMode, toggleMode }), [mode, effectiveMode, setMode, toggleMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error("useThemeMode must be used within ThemeModeProvider");
  return ctx;
}
